/* eslint-disable no-unsafe-optional-chaining */
import {
  Breadcrumbs,
  CertificationModalLayout,
  FullscreenClipLoader,
  ModuleActionBar,
  RankModuleLayout,
  RankPointsModal,
  ReportModalLayout,
  SidePanel,
  SidePanelModal,
} from '@components';
import { routes } from '@constants';
import {
  useModuleData,
  useNavigateWithOfflineMode,
  useScreenSize,
  useScrollToTop,
} from '@hooks';
import Net from '@pngs/ModulePage/Net.png';
import { courseApi, courseSlice } from '@store';
import { Flex } from '@styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { findCheckPoint } from 'src/constants/rankPoints';

import { ActivityLayout } from './components';
import { modulePageLayoutByVariant } from './ModulePage.layoutProps';

const findNextUncompletedEntity = (entities, currentEntityId = null) => {
  const fallbackResponse =
    entities?.filter((item) => !item?.is_completed)?.[0] || entities?.[0];

  if (!currentEntityId) {
    return fallbackResponse;
  }

  const entityIndex = entities?.findIndex(
    (entity) => entity?.id === currentEntityId
  );

  // findIndex returns -1 if no entity was found
  if (entityIndex < 0) return fallbackResponse;

  const uncompletedEntitiesBeforeCurrentOne = entities
    ?.slice(0, entityIndex)
    .filter((item) => !item?.is_completed);

  const uncompletedEntitiesAfterCurrentOne = entities
    ?.slice(entityIndex + 1)
    .filter((item) => !item?.is_completed);

  return (
    uncompletedEntitiesAfterCurrentOne?.[0] ||
    uncompletedEntitiesBeforeCurrentOne?.[0] ||
    entities?.[0]
  );
};

const findNextCompletedEntity = (entities, currentEntityId = null) => {
  const fallbackResponse = entities?.[0];

  if (!currentEntityId) {
    return fallbackResponse;
  }

  const entityIndex = entities?.findIndex(
    (entity) => entity?.id === currentEntityId
  );

  // findIndex returns -1 if no entity was found
  if (entityIndex < 0) return fallbackResponse;

  return entities?.slice(entityIndex + 1)?.[0] || fallbackResponse;
};

const ModulePage = () => {
  const { navigate } = useNavigateWithOfflineMode();
  const { i18n } = useTranslation();
  const [pointsContent, setPointsContent] = useState(0);
  const dispatch = useDispatch();
  const location = useLocation();
  const { device, isTabletLG } = useScreenSize();
  const { moduleId } = useParams();
  const [isReportModalVisible, setReportModalVisible] = useState(false);
  const [isRankModalVisible, setRankModalVisible] = useState(false);
  const [isRankPointsModalVisible, setRankPointsModalVisible] = useState(false);
  const [rankModalContent, setRankModalContent] = useState(null);
  const [isCertificationModalVisible, setCertificationModalVisible] =
    useState(false);
  const [shouldGoBack, setShouldGoBack] = useState(false);
  const [shouldShowCertification, setShouldShowCertification] = useState(false);

  const [isSidePanelVisible, setSidePanelVisible] = useState(false);
  const [moduleProgress, setModuleProgress] = useState(0);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentActivity, setCurrentActivity] = useState(null);
  // const [isTranscriptShown, setIsTranscriptShown] = useState(false);

  const layout = modulePageLayoutByVariant[device || 'desktop'];

  const {
    activity,
    getNextRoute,
    isOffline,
    isUpdateProgressMutationLoading,
    module,
    refetchActivity,
    refetchModule,
    refetchTest,
    refetchUser,
    test,
    updateProgress,
    userData,
  } = useModuleData({
    currentActivity,
    moduleId,
    setCurrentActivity,
    setCurrentLesson,
  });

  const rankCheckPoint = useMemo(
    () => userData?.rank_points && findCheckPoint(userData?.rank_points),
    [userData?.rank_points]
  );

  const { course, refetch: refetchCourse } = courseApi.useGetCourseByIdQuery(
    { id: module?.course_id },
    {
      refetchOnMountOrArgChange: !isOffline && module?.course_id,
      selectFromResult: ({ data }) => ({
        course: data?.data,
      }),
      skip: !module?.course_id,
    }
  );

  useScrollToTop();

  useEffect(() => {
    if (course && !isOffline) refetchCourse();
  }, [i18n.language]);

  useEffect(() => {
    if (course) {
      dispatch(courseSlice.actions.setCourseById(course));
    }
  }, [course]);

  useEffect(() => {
    if (device === 'desktop' || device === 'laptop') {
      setSidePanelVisible(true);
    }
  }, [device]);

  useEffect(() => {
    if (module && !isUpdateProgressMutationLoading) {
      dispatch(courseSlice.actions.setModuleById(module));
    }
  }, [module, isUpdateProgressMutationLoading]);

  useEffect(() => {
    if (!isOffline && module && currentActivity) {
      const testId = module?.test_id || test?.id;
      if (testId) refetchTest({ id: testId });

      refetchModule();
      refetchActivity();
    }
  }, [i18n.language, isOffline]);

  useEffect(() => {
    if (module && !isUpdateProgressMutationLoading) {
      const firstLessonInModule = module?.lessons?.[0];
      const firstActivityInFirstLesson = firstLessonInModule?.activities?.[0];

      /* 
        Case 1: Presence of location.state.restart
        This indicates already completed module which user passes for another time
        Setting both lesson and activity to first
      */
      if (location?.state?.restart) {
        setCurrentLesson(firstLessonInModule);
        setCurrentActivity(firstActivityInFirstLesson);

        // clear location state after data from there was used
        navigate(location.pathname, { replace: true });
        return;
      }

      if (test && location?.state?.finalTest) {
        /* 
          Case 2: Presence of location.state.finalTest
          This indicates user is about to pass final module test
        */
        setCurrentLesson({ ...test, content_type: 'test' });
        setCurrentActivity({ ...test, content_type: 'test' });
        setShouldGoBack(true);

        // clear location state after data from there was used
        navigate(location.pathname, { replace: true });

        return;
      }

      /*
        Case 3.1: Presence of location.state.lessonId
        This indicates user is redirected from another page (Learning Map or User Profile -> Ongoing Module)
        Setting lesson and activity based on the passed lessonId

        Case 3.2: no additional stateLocation params, setting lesson and activity based on module.current_lesson
      */

      const lessonIdToRedirectTo = location?.state?.lessonId;

      // Clear location state after usage
      navigate(location.pathname, { replace: true });

      const firstUncompletedLessonInModule = findNextUncompletedEntity(
        module?.lessons
      );

      const idOfLessonToOpen =
        lessonIdToRedirectTo ||
        currentLesson?.id ||
        firstUncompletedLessonInModule?.id;

      const indexOfLessonToOpen = module?.lessons?.findIndex(
        (lesson) => lesson?.id === idOfLessonToOpen
      );

      const lessonToOpen = module?.lessons?.[indexOfLessonToOpen];

      const isSomeActivityAlreadyOpened = currentLesson && currentActivity;

      // Setting lesson & activitity to first if lessonToOpen couldn't be determined
      if (!lessonToOpen && !isSomeActivityAlreadyOpened) {
        setCurrentLesson(firstLessonInModule);
        setCurrentActivity(firstActivityInFirstLesson);
        return;
      }

      // Make sure to prevent unnecessary rerenders
      if (currentLesson && currentActivity) {
        return;
      }

      setModuleProgress(
        (module?.lessons.filter((lesson) => lesson.is_completed).length /
          module?.lessons?.length) *
          100
      );

      const activityToOpen = findNextUncompletedEntity(lessonToOpen.activities);

      if (lessonToOpen?.id !== currentLesson?.id) {
        setCurrentLesson(lessonToOpen);
      }

      if (activityToOpen?.id !== currentActivity?.id) {
        setCurrentActivity(activityToOpen);
      }
    }
  }, [module, isUpdateProgressMutationLoading, test, location?.state]);

  const handleReportButtonClick = () => {
    setReportModalVisible(true);
  };

  const toggleSidePanel = () => {
    setSidePanelVisible((prevState) => !prevState);
  };

  const openRankModal = () => setRankModalVisible(true);

  const openCertificationModal = () => setCertificationModalVisible(true);

  const clearRank = () => {
    setRankModalContent(null);
  };

  const goBack = () => {
    if (!isOffline) navigate(routes.learningMap);
  };

  const onDropdownPress =
    ({ activityId, isFinalTest, lessonId }) =>
    () => {
      if (isFinalTest) {
        setCurrentLesson({ ...test, content_type: 'test' });
        setCurrentActivity({ ...test, content_type: 'test' });
      } else {
        const lessonIndex = module?.lessons?.findIndex(
          (lesson) => lesson?.id === lessonId
        );
        setCurrentLesson(module?.lessons?.[lessonIndex]);
        const currentActivityIndex = module?.lessons?.[
          lessonIndex
        ]?.activities?.findIndex((item) => item?.id === activityId);
        setCurrentActivity(
          module?.lessons?.[lessonIndex]?.activities?.[currentActivityIndex]
        );
      }
    };

  const updateProgressBasedOnStatus = async (activityId) => {
    if (isOffline) {
      const result = await updateProgress({
        activityId,
      });
      const data = result?.data;

      const points = data?.rank_points_received;
      if (points) {
        setPointsContent(points);
        setTimeout(() => {
          setPointsContent(0);
        }, 5000);
      }
      return data;
    }
    const { data } = await updateProgress({
      activityId,
    }).unwrap();
    if (data?.unlocked_entities?.new_rank) {
      setRankModalContent(data?.unlocked_entities);
    }
    const points = data?.rank_points_received;
    await refetchUser();
    if (points) {
      setPointsContent(points);
      setTimeout(() => {
        setPointsContent(0);
      }, 5000);
    }
    return data;
  };

  const handleLogActivity =
    ({ activityId, isFinalTest, lessonId }) =>
    async () => {
      /* 
        First thing is to send request to BE to record activity completion
      */
      if (activityId) {
        await updateProgressBasedOnStatus(activityId);
        if (!isOffline) {
          refetchModule();
          refetchActivity();
        }
      } else if (!isFinalTest) {
        return;
      }
      const areAllOtherModulesCompletedInCourse = course?.modules
        ?.filter((item) => item.id !== module?.id)
        .every((item) => item?.is_completed);
      const isLastModule =
        course?.modules.findIndex((item) => item.id === module?.id) ===
        course?.modules?.length - 1;

      // Exit case 1: module final test was just passed
      if (isFinalTest) {
        if (areAllOtherModulesCompletedInCourse) {
          setShouldShowCertification(true);
          if (isLastModule) {
            navigate(`/courses/${module?.course_id}/certification`, {
              state: {
                id: course?.test_id,
              },
            });
          }
        } else {
          setShouldGoBack(true);
        }

        navigate(getNextRoute(), {
          state: { restart: true },
        });

        window.scrollTo(0, 0);
        return;
      }

      const openedLesson = module?.lessons.find(
        (item) => item?.id === lessonId
      );

      const activitiesOfOpenedLesson = openedLesson?.activities;

      const isLessonCompletedNow = activitiesOfOpenedLesson
        ?.filter((item) => item.id !== activityId)
        .every((item) => item?.is_completed);

      const areAllOtherLessonsCompletedInModule = module?.lessons
        .filter((item) => item.id !== lessonId)
        .every((item) => item?.is_completed);
      const isLastAcivityInLesson =
        activitiesOfOpenedLesson?.indexOf(
          activitiesOfOpenedLesson?.find((item) => item.id === activityId)
        ) ===
        activitiesOfOpenedLesson?.length - 1;
      /* 
        Exit case 1.2: user completed lesson in the module that is completed
      */
      if (
        areAllOtherLessonsCompletedInModule &&
        isLastAcivityInLesson &&
        module?.is_completed
      ) {
        const lessonToOpenNext = findNextCompletedEntity(
          module?.lessons,
          lessonId
        );

        const activityToOpenNext = findNextCompletedEntity(
          lessonToOpenNext?.activities
        );

        setCurrentLesson(lessonToOpenNext);
        setCurrentActivity(activityToOpenNext);
        window.scrollTo(0, 0);
        return;
      }
      // Exit case 1.3: user completed activity in the module that is completed
      if (areAllOtherLessonsCompletedInModule && module?.is_completed) {
        const activityToOpenNext = findNextCompletedEntity(
          activitiesOfOpenedLesson,
          activityId
        );

        setCurrentLesson(openedLesson);
        setCurrentActivity(activityToOpenNext);
        window.scrollTo(0, 0);
        return;
      }
      /* 
        Exit case 2: user just completed last uncompleted lesson in module
      */
      if (isLessonCompletedNow && areAllOtherLessonsCompletedInModule) {
        if (test || module?.test_id) {
          navigate(`/courses/${module?.course_id}/${module?.id}`, {
            state: { finalTest: true },
          });
        } else if (isLastModule) {
          setShouldShowCertification(true);
          navigate(`/courses/${module?.course_id}/certification`, {
            state: {
              id: course?.test_id,
            },
          });
        } else {
          setShouldGoBack(true);
        }
        window.scrollTo(0, 0);
        return;
      }

      /* 
        Exit case 3: user just completed the lesson, 
        but there are still uncompleted lessons in the module

        Redirect user to the next uncompleted lesson in the module
      */
      if (isLessonCompletedNow) {
        // Either redirect user to next uncompleted lesson in order or to the first uncompleted lesson in order
        const lessonToOpenNext = findNextUncompletedEntity(
          module?.lessons,
          lessonId
        );

        const activityToOpenNext = findNextUncompletedEntity(
          lessonToOpenNext?.activities
        );

        setCurrentLesson(lessonToOpenNext);
        setCurrentActivity(activityToOpenNext);
        window.scrollTo(0, 0);
        return;
      }

      /* 
        Exit case 4: user just completed the activity, 
        but there are still uncompleted activities in the lesson
      */
      const activityToOpenNext = findNextUncompletedEntity(
        activitiesOfOpenedLesson,
        activityId
      );

      setCurrentLesson(openedLesson);
      setCurrentActivity(activityToOpenNext);
      window.scrollTo(0, 0);
    };

  if (!module) return <FullscreenClipLoader />;
  const handleRankButtonClick = () => {
    setRankPointsModalVisible(true);
  };

  return (
    <Flex backgroundImage={Net} backgroundSize="cover" flexDirection="column">
      <RankPointsModal
        isVisible={isRankPointsModalVisible}
        rank={userData?.rank}
        rankCheckPoint={rankCheckPoint}
        rankPoints={+userData?.rank_points}
        setModalVisible={setRankPointsModalVisible}
      />
      <CertificationModalLayout
        courseId={module?.course_id}
        id={course?.test_id}
        isVisible={isCertificationModalVisible}
        setIsVisible={setCertificationModalVisible}
      />
      <ReportModalLayout
        activityId={currentActivity?.id}
        isLessonActivity
        isVisible={isReportModalVisible}
        setIsVisible={setReportModalVisible}
        withBackdropNoise
      />
      <RankModuleLayout
        isVisible={isRankModalVisible}
        // newActivities={rankModalContent?.new_activities}
        onClose={
          // eslint-disable-next-line no-nested-ternary
          shouldGoBack
            ? goBack
            : shouldShowCertification
              ? openCertificationModal
              : clearRank
        }
        rank={rankModalContent?.new_rank}
        setIsVisible={setRankModalVisible}
      />
      {isTabletLG && (
        <SidePanelModal
          currentActivity={currentActivity}
          currentLesson={currentLesson}
          finalTest={test}
          isOpen={isSidePanelVisible}
          lessons={module?.lessons}
          onActivityPress={onDropdownPress}
          onLessonPress={onDropdownPress}
          setIsOpen={setSidePanelVisible}
        />
      )}
      <Breadcrumbs containerProps={{ px: layout?.px }} />
      <Flex flexDirection="column" px={layout?.px}>
        <ModuleActionBar
          additionalButton={{
            // isVisible: currentActivity?.content_type === 'theory',
            // onClick: toggleTranscript,
            // variant: 'text',
            isVisible: false,
          }}
          progress={{ isSemivisible: true, percentage: moduleProgress }}
          rank={{
            data: userData?.rank
              ? { ...userData?.rank, points: pointsContent }
              : null,
            isVisible: true,
            onClick: handleRankButtonClick,
            variant: 'rank',
          }}
          report={{
            isVisible: true,
            onClick: handleReportButtonClick,
            variant: 'report',
          }}
          sidePanel={{
            isVisible: true,
            onClick: toggleSidePanel,
            variant: isSidePanelVisible ? 'sidePanelOpen' : 'sidePanelClosed',
          }}
        />
        <Flex pb={80} pt={24}>
          {!isTabletLG && (
            <SidePanel
              currentActivity={currentActivity}
              currentLesson={currentLesson}
              finalTest={test}
              isOpen={isSidePanelVisible}
              lessons={module?.lessons}
              onActivityPress={onDropdownPress}
              onLessonPress={onDropdownPress}
              setIsOpen={setSidePanelVisible}
            />
          )}
          {activity && (
            <ActivityLayout
              activity={isOffline ? currentActivity : activity}
              finalTest={test && { ...test, module_id: module?.id }}
              isLoading={isUpdateProgressMutationLoading}
              onFinish={handleLogActivity({
                activityId: currentActivity?.id,
                isFinalTest: currentLesson?.content_type === 'test',
                lessonId: currentLesson?.id,
              })}
              openRankModal={openRankModal}
              setRankModalContent={setRankModalContent}
              type={currentActivity?.content_type}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ModulePage;
